import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { SendOutEmailsModalStore } from 'store/general/sendOutEmailsModalStore';
import { ProposalDomainParams } from 'store/modules/proposal/proposalDomainModule';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { getProposalDownloadURL, shareProposal as requestShareProposal } from 'api/operations/proposalServiceV3';
import { ProposalFileType } from 'types/proposalService';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';

interface DomainParams extends ProposalDomainParams {
  url: string;
  catalogId: string;
  proposalType: 'SHELF' | 'RFQ';
  cartName: string;
}

export default class ProposalReadyPageStore {
  private _domainParams: DomainParams;
  private _isLoading = false;
  private _isInitialLoading = true;
  private _pdfFilePath: string | null = null;
  private _xlsxFilePath: string | null = null;
  private _enrollmentModule = new EnrollmentPeriodModule();
  public sendOutEmailsModalStore = new SendOutEmailsModalStore({ onSubmit: this.shareProposal.bind(this) });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isInitialLoading(): boolean {
    return this._isInitialLoading;
  }

  public get pdfFilePath(): string | null {
    return this._pdfFilePath;
  }

  public get xlsxFilePath(): string | null {
    return this._xlsxFilePath;
  }

  public get isProposalConfirmed(): boolean {
    if (!this._enrollmentModule.enrollmentProposalData) {
      return false;
    }
    return this._enrollmentModule.getProposalById(this._domainParams.cartId).confirmed;
  }

  public setIsInitialLoading(status: boolean): void {
    this._isInitialLoading = status;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialLoading(true);

    const [pdf, xlsx] = await Promise.all([
      getProposalDownloadURL(this._domainParams.cartId, ProposalFileType.PDF),
      getProposalDownloadURL(this._domainParams.cartId, ProposalFileType.SHEET),
      this._enrollmentModule.uploadEnrollmentPeriod(this._domainParams.enrollmentPeriodId),
    ]);

    runInAction(() => {
      this._pdfFilePath = pdf;
      this._xlsxFilePath = xlsx;
      this.setIsInitialLoading(false);
    });
  }

  public redirectBack(): void {
    if (this._domainParams.proposalType === 'SHELF') {
      bpHistory.push(`/proposal-v2/${this._domainParams.url}`);
    } else {
      bpHistory.push(`/rfq-proposal/${this._domainParams.url}&type=release`);
    }
  }

  public async shareProposal(recipientEmails: string[], message?: string): Promise<void> {
    this.setIsLoading(true);

    const [primary, ...ccs] = recipientEmails;

    await requestShareProposal(
      this._domainParams.cartId,
      this._domainParams.groupId,
      this._domainParams.cartName,
      primary,
      ccs,
      message,
    );

    runInAction(() => {
      this.setIsLoading(false);
      this.sendOutEmailsModalStore.clearModalData();
      this.sendOutEmailsModalStore.close();
      toastManager.addToast('The proposal has been shared!', ToastColorTypes.SUCCESS);
    });
  }

  constructor(domainParams: DomainParams) {
    this._domainParams = domainParams;
    makeObservable<
      ProposalReadyPageStore,
      '_isLoading' | '_isInitialLoading' | '_pdfFilePath' | '_xlsxFilePath' | '_enrollmentModule'
    >(this, {
      _isLoading: observable,
      _isInitialLoading: observable,
      _pdfFilePath: observable,
      _xlsxFilePath: observable,
      _enrollmentModule: observable,
      sendOutEmailsModalStore: observable,
      isLoading: computed,
      isInitialLoading: computed,
      pdfFilePath: computed,
      xlsxFilePath: computed,
      isProposalConfirmed: computed,
      setIsLoading: action.bound,
      setIsInitialLoading: action.bound,
      getInitialData: action.bound,
      redirectBack: action.bound,
      shareProposal: action.bound,
    });
  }
}

export const ProposalReadyPageStoreContext = createContext<ProposalReadyPageStore | null>(null);

export function useProposalReadyPageStore(): ProposalReadyPageStore {
  const store = useContext(ProposalReadyPageStoreContext);
  if (!store) {
    throw new Error('useProposalReadyPageStore must be used within ProposalReadyPageStoreContext.Provider');
  }
  return store;
}
